/**
 * The state transitions behind `CodeField`, as pure functions.
 *
 * Separated from the component because this is where the subtle behaviour
 * lives — where the caret lands, what a gap means, when a code counts as
 * finished — and none of it needs a DOM to be true. A rendered test would
 * have to stand up React and a document to assert on a string.
 *
 * The value is a fixed-width string with a space standing for an empty cell,
 * trailing spaces trimmed. That representation is what makes a gap possible
 * ("12 456"), and a gap is what makes `isComplete` a real question rather
 * than a length check.
 */

/** Everything that is not a digit is dropped rather than rejected loudly. */
export const onlyDigits = (s: string) => s.replace(/\D/g, "");

const pad = (value: string, length: number) => value.padEnd(length, " ");

/**
 * Every cell holds a digit.
 *
 * NOT `value.length === length`. A code with an interior gap is exactly that
 * long — "12 456" is six characters — because trimming only removes trailing
 * spaces. Reaching the last cell by arrow key and typing there produced a
 * five-digit code that reported itself finished.
 */
export function isComplete(value: string, length: number): boolean {
  return value.length === length && onlyDigits(value).length === length;
}

/** The cell the caret belongs in: the first empty one, or -1 when full. */
export function firstGap(value: string, length: number): number {
  for (let i = 0; i < length; i++) {
    if (!(value[i] ?? "").trim()) return i;
  }
  return -1;
}

/** Write digits starting at `from`, and say where the caret should land. */
export function writeAt(
  value: string,
  length: number,
  from: number,
  digits: string,
): { next: string; caret: number } {
  const padded = pad(value, length);
  const next = (
    padded.slice(0, from) +
    digits +
    padded.slice(from + digits.length)
  )
    .slice(0, length)
    .trimEnd();
  return { next, caret: Math.min(from + digits.length, length - 1) };
}

/** Empty one cell, leaving a gap rather than closing up. */
export function clearAt(value: string, length: number, index: number): string {
  const padded = pad(value, length);
  return `${padded.slice(0, index)} ${padded.slice(index + 1)}`.trimEnd();
}

/**
 * Backspace. On a filled cell the browser's own deletion handles it; on an
 * empty one this steps back and clears, which is what a single field did for
 * free and six inputs have to be told to do.
 */
export function backspaceAt(
  value: string,
  length: number,
  index: number,
): { next: string; caret: number } {
  const back = Math.max(0, index - 1);
  return { next: clearAt(value, length, back), caret: back };
}
